import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { CtaRow, GhostCta, PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';
import { PIXEL_ART, PixelAvatar } from './LandingPixels';

const BOTS = [
  {
    name: 'Video creator', specialty: 'Bring your stories to life', icon: 'film', avatar: PIXEL_ART.creative, kind: 'video',
    instructions: 'Use cinematic visuals, natural pacing, and music that fits the mood. Keep the story at the center.',
    job: 'From a simple idea to a finished video.',
    prompt: 'Create a 30-second film about escaping into the mountains. Add an inspiring voiceover and a cinematic soundtrack.',
    response: 'Your mountain escape film brings the scenery, voiceover, and music together in one video.',
    document: 'A little further from ordinary', format: 'VIDEO · MP4',
    content: ['A story in three scenes', 'The road out. The climb up. A sunrise worth the journey.'],
    note: 'Cinematic scenes · Voiceover · Original soundtrack',
  },
  {
    name: 'Game developer', specialty: 'An idea you can play', icon: 'gamepad', avatar: PIXEL_ART.game, kind: 'game',
    instructions: 'Make games easy to pick up and fun to replay. Include clear controls, scoring, and a restart option.',
    job: 'Dream up a game. Put it in motion.',
    prompt: 'Build a space arcade game where I dodge asteroids and collect stars. Give it a retro look and make it work on my phone.',
    response: 'Star Runner is ready to play, with touch controls, a score counter, and another try whenever you need it.',
    document: 'Star Runner', format: 'VIDEO GAME · HTML',
    content: ['Dodge. Collect. Beat your best.', 'A space adventure with touch and keyboard controls.'],
    note: 'Playable game · Mobile controls · Downloadable source',
  },
  {
    name: 'Web developer', specialty: 'Your own corner of the web', icon: 'code', avatar: PIXEL_ART.developer, kind: 'website',
    instructions: 'Build clean, responsive websites with readable text, accessible navigation, and a distinctive visual style.',
    job: 'Take your idea from a prompt to a website.',
    prompt: 'Build a website for my hiking club. Include our next adventures, a section about the club, and a way to get in touch.',
    response: 'Here’s a website for your hiking club, with upcoming adventures and a layout that adapts to phones and desktops.',
    document: 'The Weekend Trail Club', format: 'WEBSITE · HTML & CSS',
    content: ['Find your next adventure.', 'A home for your club, your projects, or whatever you want to share.'],
    note: 'Responsive layout · Website preview · Editable source',
  },
  {
    name: 'Creative writer', specialty: 'Make room for imagination', icon: 'pen', avatar: PIXEL_ART.writer, kind: 'writing',
    instructions: 'Write vivid, original stories with memorable characters. Keep the language playful and the ending hopeful.',
    job: 'Give your next story a first chapter.',
    prompt: 'Write the opening of a bedtime story about a small fox who collects fallen stars.',
    response: 'Here’s the beginning of a little adventure, with a curious fox and a sky full of possibilities.',
    document: 'The fox who borrowed the sky', format: 'STORY · FIRST DRAFT',
    content: ['Every night, a star fell into the garden.', 'And every morning, Pip put it in his pocket.', 'By Thursday, his coat was glowing. By Friday, the moon had noticed.'],
    note: 'Original stories · Your preferred style · Ready to revise',
  },
];
const BENEFITS = [
  { icon: 'pen', title: 'Explain it once.', copy: 'Give each AI employee a role, your preferences, and clear instructions. Spend less time repeating yourself when the next task comes along.' },
  { icon: 'robot', title: 'Hand off the next task.', copy: 'Keep your AI employees in the sidebar. Give them an assignment, let them create, and come back to review the result.' },
  { icon: 'magnifying-glass', title: 'Make room for more.', copy: 'A personal project, an everyday chore, or a big idea. Hire a community bot or create an AI employee for whatever you want to get done.' },
];

// Illustrative output artwork, not embedded editors or live playback controls.
function OutputPreview({ kind }) {
  if (kind === 'writing') return null;
  return (
    <View aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ height: 155, borderRadius: 6, overflow: 'hidden', marginBottom: 20, backgroundColor: '#14251D' }}>
      {kind === 'game' ? (
        <View style={{ flex: 1, padding: 16, backgroundColor: '#131D30', justifyContent: 'space-between' }}>
          {Array.from({ length: 18 }, (_, i) => <View key={i} style={{ position: 'absolute', left: `${(i * 37 + 9) % 96}%`, top: `${(i * 23 + 12) % 92}%`, width: 3, height: 3, backgroundColor: '#A7BBD7', opacity: 0.6 }} />)}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text weight="semibold" style={{ color: '#C8DAF1', fontSize: 10, letterSpacing: 1.3 }}>STAR RUNNER</Text>
            <Text style={{ color: '#91E2B8', fontSize: 10 }}>SCORE 0240</Text>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-evenly' }}>
            {[0, 1, 2, 3].map(i => <View key={i} style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: '#857596', transform: [{ rotate: '25deg' }, { translateY: i % 2 ? 10 : -4 }] }} />)}
          </View>
          <View style={{ alignItems: 'center' }}><Icon name="rocket" size={25} color="#91E2B8" /></View>
        </View>
      ) : (
        <>
          <Image source={require('@/assets/images/landing-mountain.png')} contentFit="cover" style={StyleSheet.absoluteFillObject} />
          <View style={{ flex: 1, backgroundColor: 'rgba(5,20,13,0.48)', padding: 18, justifyContent: 'space-between' }}>
            <Text weight="medium" style={{ color: '#FFFFFF', fontSize: 10, letterSpacing: 1.4 }}>{kind === 'video' ? 'A LITTLE FURTHER FROM ORDINARY' : 'THE WEEKEND TRAIL CLUB'}</Text>
            {kind === 'video' ? (
              <View style={{ gap: 9 }}>
                <Text style={{ color: '#FFFFFF', fontSize: 17 }}>There’s a whole world out here.</Text>
                <View style={{ height: 3, backgroundColor: 'rgba(255,255,255,.35)', borderRadius: 2 }}><View style={{ width: '38%', height: 3, backgroundColor: '#FFFFFF' }} /></View>
                <Text style={{ color: '#FFFFFF', fontSize: 10 }}>00:12 / 00:30</Text>
              </View>
            ) : (
              <View style={{ gap: 9 }}>
                <Text weight="semibold" style={{ color: '#FFFFFF', fontSize: 24, lineHeight: 28 }}>Weekends are for wandering.</Text>
                <Text style={{ color: '#FFFFFF', fontSize: 11 }}>Find a trail. Bring a friend.</Text>
              </View>
            )}
          </View>
        </>
      )}
    </View>
  );
}

export default function LandingBots() {
  const { c, width, isCompact, isXS, isDark } = useLanding();
  const [selected, setSelected] = useState(0);
  const bot = BOTS[selected];
  const headingSize = fluid(width, 40, 68);
  const surface = isDark ? '#18231D' : '#FFFFFF';
  const inset = isDark ? '#202E25' : '#F3F5EF';

  return (
    <Section label="Meet Gab AI Bots" background={isDark ? '#151D18' : '#F0F3EC'} contentStyle={{ paddingTop: fluid(width, 56, 88), paddingBottom: fluid(width, 52, 76) }}>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'stretch' : 'flex-end', gap: 28 }}>
        <View style={{ flex: isCompact ? undefined : 1.2, minWidth: 0 }}>
          <View style={styles.eyebrow}>
            <Icon name="robot" size={15} color={c.accent} />
            <Text weight="semibold" style={{ color: c.accent, fontSize: 11, letterSpacing: 1.8 }}>GAB AI BOTS</Text>
          </View>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: headingSize, lineHeight: Math.round(headingSize * 1.06), letterSpacing: -headingSize * 0.045, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>Build your{'\n'}AI Team.</Text>
        </View>
        <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, maxWidth: 470 }}>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: fluid(width, 17, 19), lineHeight: 29 }}>Gab Bots are your AI employees. Build a team to create videos, games, websites, stories, and more. Give them your instructions and hand off the tasks, so you can make time for what matters to you.</Text>
          <CtaRow style={{ marginTop: 24 }}>
            <PrimaryCta label="Hire your first AI employee" href="/agents" restrained full={isXS} />
            <GhostCta label="How hiring works" href="/docs/agents" />
          </CtaRow>
        </View>
      </View>

      <View style={{ marginTop: fluid(width, 32, 44) }}>
        <Text style={{ color: c.muted, fontSize: 12, lineHeight: 19, marginBottom: 13 }}>Meet your next AI employee. Choose a role to see a sample assignment.</Text>
        <View accessibilityRole={isWeb ? 'group' : undefined} accessibilityLabel="Choose a bot to preview" style={styles.choices}>
          {BOTS.map((item, index) => {
            const active = selected === index;
            return (
              <Pressable key={item.name} accessibilityRole="button" accessibilityLabel={`Preview ${item.name}`} {...(isWeb ? { 'aria-pressed': active, dataSet: { landingBotChoice: 'true' } } : { accessibilityState: { selected: active } })} onPress={() => setSelected(index)} style={({ hovered, pressed }) => [styles.botChoice, { flexBasis: isCompact ? '47%' : 0, padding: isXS ? 12 : 18, backgroundColor: active ? c.accentFill : hovered || pressed ? c.accentWash : surface, borderColor: active ? c.accentFill : c.ruleStrong }]}>
                <PixelAvatar source={item.avatar} size={isXS ? 48 : 76} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text weight="semibold" style={{ color: active ? '#FFFFFF' : c.ink, fontSize: isXS ? 12 : 14, lineHeight: 20 }}>{item.name}</Text>
                  {!isXS && <Text style={{ color: active ? '#DBE9DF' : c.inkSoft, fontSize: 11, lineHeight: 17, marginTop: 3 }}>{item.specialty}</Text>}
                </View>
                {!isCompact && <Icon name={active ? 'check' : 'arrow-right'} size={12} color={active ? '#FFFFFF' : c.muted} />}
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={[styles.stage, { marginTop: 16, backgroundColor: surface, borderColor: c.ruleStrong }]}>
        <View style={[styles.toolbar, { borderColor: c.rule, backgroundColor: inset }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Icon name="robot" size={13} color={c.accent} />
            <Text weight="medium" style={{ color: c.inkSoft, fontSize: 12 }}>Your AI employees</Text>
          </View>
          <Text style={{ color: c.muted, fontSize: 11 }}>Illustrative example</Text>
        </View>
        <View {...(isWeb ? { 'aria-live': 'polite', 'aria-atomic': true } : {})} style={{ flexDirection: isCompact ? 'column' : 'row' }}>
          <View style={{ flex: isCompact ? undefined : 0.85, minWidth: 0, padding: isXS ? 22 : 32, backgroundColor: inset, borderRightWidth: isCompact ? 0 : 1, borderBottomWidth: isCompact ? 1 : 0, borderColor: c.rule }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 22 }}>
              <PixelAvatar source={bot.avatar} size={isXS ? 92 : 120} />
              <View style={{ flex: 1 }}>
                <Text weight="semibold" style={{ color: c.ink, fontSize: 18, lineHeight: 25 }}>{bot.name}</Text>
                <Text style={{ color: c.inkSoft, fontSize: 12, marginTop: 3 }}>{bot.specialty}</Text>
              </View>
            </View>
            <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: fluid(width, 24, 30), lineHeight: isXS ? 30 : 37, letterSpacing: -0.8 }}>{bot.job}</Text>
            <View style={{ borderLeftWidth: 2, borderColor: c.accent, paddingLeft: 16, marginTop: 26 }}>
              <Text weight="semibold" style={{ color: c.accent, fontSize: 10, letterSpacing: 1.3, marginBottom: 9 }}>THE INSTRUCTIONS YOU SET</Text>
              <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 23 }}>{bot.instructions}</Text>
            </View>
            <View style={{ marginTop: 28, paddingTop: 20, borderTopWidth: 1, borderColor: c.rule }}>
              <Text weight="medium" style={{ color: c.muted, fontSize: 10, letterSpacing: 1.3, marginBottom: 9 }}>THE TASK YOU BRING</Text>
              <Text style={{ color: c.ink, fontSize: 14, lineHeight: 24 }}>{bot.prompt}</Text>
            </View>
          </View>
          <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0, padding: isXS ? 22 : 32 }}>
            <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 23, marginBottom: 22 }}>{bot.response}</Text>
            <View style={[styles.document, { padding: isXS ? 22 : 28, minHeight: isCompact ? undefined : 300, backgroundColor: isDark ? '#1C2820' : '#FCFCF8', borderColor: c.ruleStrong }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <Icon name={bot.icon} size={12} color={c.accent} />
                <Text weight="semibold" style={{ color: c.accent, fontSize: 10, letterSpacing: 1.5 }}>{bot.format}</Text>
              </View>
              <OutputPreview kind={bot.kind} />
              <Text weight="semibold" style={{ color: c.ink, fontSize: isXS ? 23 : 28, lineHeight: 33, letterSpacing: -0.7, marginBottom: 20 }}>{bot.document}</Text>
              {bot.content.map((line, index) => {
                const heading = index === 0 || (bot.content.length === 4 && index === 2);
                return <Text key={line} weight={heading ? 'medium' : 'normal'} style={{ color: heading ? c.ink : c.inkSoft, fontSize: 14, lineHeight: 24, marginBottom: index === bot.content.length - 1 ? 0 : 12 }}>{line}</Text>;
              })}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginTop: 16 }}>
              <Icon name="check" size={12} color={c.accent} style={{ marginTop: 4 }} />
              <Text style={{ flex: 1, color: c.inkSoft, fontSize: 11, lineHeight: 19 }}>{bot.note}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 24 : 36, marginTop: 32 }}>
        {BENEFITS.map(item => <View key={item.title} style={{ flex: isCompact ? undefined : 1, minWidth: 0, flexDirection: 'row', gap: 13 }}>
          <View style={{ paddingTop: 3 }}><Icon name={item.icon} size={16} color={c.accent} /></View>
          <View style={{ flex: 1 }}>
            <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 16, lineHeight: 23 }}>{item.title}</Text>
            <Text style={{ color: c.inkSoft, fontSize: 13, lineHeight: 22, marginTop: 6 }}>{item.copy}</Text>
          </View>
        </View>)}
      </View>
    </Section>
  );
}
const styles = StyleSheet.create({
  eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 22 },
  choices: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  botChoice: { flexGrow: 1, borderRadius: 12, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 11, minHeight: 72, ...(isWeb ? { cursor: 'pointer' } : {}) },
  stage: { borderRadius: 18, borderWidth: 1, overflow: 'hidden' },
  toolbar: { minHeight: 45, borderBottomWidth: 1, paddingHorizontal: 22, paddingVertical: 12, flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' },
  document: { borderWidth: 1, borderRadius: 8 },
});
